/**
 * The theme a user picks for themselves: Light, Dark, or Match my device.
 *
 * Saved on the user account rather than the workshop, so it follows them
 * across devices and survives a sign-out, and one person going dark does not
 * drag the rest of the workshop with them. localStorage holds a copy only so
 * the first paint after a reload is already right, before /auth/me answers.
 *
 * The dark class lands on <html> — dialogs and dropdowns portal to <body>, so
 * a wrapper div would leave them stranded in light mode. It is applied only
 * while a console screen is mounted, which is what keeps the public site, the
 * customer payment page and the Driveline pages out of it.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import axios from 'axios';
import { setAccentDark } from '../lib/accentTheme';
import { API } from '../lib/api';
import { useAuth } from './AuthContext';

const STORE_KEY = 'driveline:theme';
const CHOICES = ['light', 'dark', 'system'];

const readStored = () => {
  try {
    const v = window.localStorage.getItem(STORE_KEY);
    return CHOICES.includes(v) ? v : 'light';
  } catch { return 'light'; }
};

const prefersDark = () => typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-color-scheme: dark)').matches;

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const { user, authHeader } = useAuth();
  const [theme, setTheme] = useState(readStored);
  const [deviceDark, setDeviceDark] = useState(prefersDark);
  // How many console screens are mounted. Nothing outside the console ever
  // asks for the theme, so this is how the public pages stay light.
  const [scopes, setScopes] = useState(0);

  // The account is the source of truth once it arrives. A user who never
  // chose one gets Light rather than inheriting whatever the last person to
  // use this browser picked.
  useEffect(() => {
    if (!user) return;
    const saved = CHOICES.includes(user.theme) ? user.theme : 'light';
    setTheme(saved);
    try { window.localStorage.setItem(STORE_KEY, saved); } catch { /* private mode */ }
  }, [user]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setDeviceDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const dark = (theme === 'dark' || (theme === 'system' && deviceDark)) && scopes > 0;

  // Signing out drops the choice with the session, so the next person on a
  // shared workshop computer does not inherit it.
  const hadUser = React.useRef(false);
  useEffect(() => {
    if (user) { hadUser.current = true; return; }
    if (!hadUser.current) return;
    hadUser.current = false;
    setTheme('light');
    try { window.localStorage.removeItem(STORE_KEY); } catch { /* private mode */ }
  }, [user]);

  useEffect(() => {
    const root = document.documentElement;
    // `dark` for Tailwind's own variant, `app-dark` for the utility remap.
    root.classList.toggle('app-dark', dark);
    root.classList.toggle('dark', dark);
    // A dark brand colour would disappear against a dark background, so the
    // accent is re-derived rather than left to glow.
    setAccentDark(dark);
  }, [dark]);

  const choose = useCallback(async (next) => {
    if (!CHOICES.includes(next)) return;
    setTheme(next);
    try { window.localStorage.setItem(STORE_KEY, next); } catch { /* private mode */ }
    try {
      await axios.patch(`${API}/auth/preferences`, { theme: next },
        { headers: authHeader() });
    } catch { /* the local choice still stands for this device */ }
  }, [authHeader]);

  const enterConsole = useCallback(() => {
    setScopes((n) => n + 1);
    return () => setScopes((n) => Math.max(0, n - 1));
  }, []);

  const value = useMemo(() => ({ theme, dark, choose, enterConsole }),
    [theme, dark, choose, enterConsole]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
};

/** Marks the screen as part of the internal console, where dark mode applies. */
export const useConsoleTheme = () => {
  const { enterConsole, dark } = useTheme();
  useEffect(() => enterConsole(), [enterConsole]);
  return dark;
};
